import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { NexusIcon } from '../shared/ui/NexusIcon';
import { Pill } from '../shared/ui';
import { statusTone } from './platformStore';
import { search as apiSearch } from '../api/platform-search.api.js';
import { RELEASE_FLAGS } from './releaseFlags';

const GROUP_ORDER = ['Organizations', RELEASE_FLAGS.PARTNERS && 'Partners', 'Platform Users'].filter(Boolean);

const ICONS = {
  'Organizations': 'Building2',
  'Partners':      'Briefcase',
  'Platform Users':'ShieldCheck',
};

const mapBackend = (raw) => {
  const items = [];
  (raw.organizations || []).forEach(o => items.push({
    group: 'Organizations', id: o.id, label: o.name,
    sub: o.industry || o.status, tone: statusTone(o.status),
    badge: o.status, href: '/platform/organizations/' + o.id,
  }));
  if (RELEASE_FLAGS.PARTNERS) {
    (raw.partners || []).forEach(p => items.push({
      group: 'Partners', id: 'p-' + p.id, label: p.name,
      sub: p.type || p.region, tone: statusTone(p.status),
      badge: p.status, href: '/platform/partners',
    }));
  }
  (raw.platformUsers || []).forEach(u => items.push({
    group: 'Platform Users', id: 'pu-' + u.id, label: u.name || u.email,
    sub: u.email, tone: 'purple', badge: u.platformRole,
    href: '/platform/users',
  }));
  return items;
};

const buildClientIndex = (data) => {
  const items = [];
  const { organizations, partners } = data.state;
  organizations.forEach(o => items.push({
    group: 'Organizations', id: o.id, label: o.name,
    sub: o.industry || o.status, tone: statusTone(o.status),
    badge: o.status, href: '/platform/organizations/' + o.id,
    keywords: (o.name + ' ' + (o.industry || '') + ' ' + (o.country || '') + ' ' + o.status).toLowerCase(),
  }));
  if (RELEASE_FLAGS.PARTNERS) {
    partners.forEach(p => items.push({
      group: 'Partners', id: 'p-' + p.id, label: p.name,
      sub: p.type || p.region, tone: statusTone(p.status),
      badge: p.status, href: '/platform/partners',
      keywords: (p.name + ' ' + (p.type || '') + ' ' + (p.region || '')).toLowerCase(),
    }));
  }
  return items;
};

export default function GlobalSearch({ data }) {
  const [open, setOpen]       = useState(false);
  const [query, setQuery]     = useState('');
  const [cursor, setCursor]   = useState(0);
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);
  const navigate = useNavigate();

  const clientIndex = useMemo(() => buildClientIndex(data), [data]);

  const closeSearch = useCallback(() => { setOpen(false); setQuery(''); setResults([]); }, []);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setOpen(v => { if (!v) { setQuery(''); setResults([]); } return !v; });
      }
      if (e.key === 'Escape') closeSearch();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [closeSearch]);

  useEffect(() => { if (open) setTimeout(() => inputRef.current?.focus(), 50); }, [open]);

  useEffect(() => {
    const q = query.trim();
    if (!q) { setResults([]); setSearching(false); return; }

    // Instant client-side results while backend loads
    const clientHits = clientIndex.filter(i => i.keywords?.includes(q.toLowerCase()));
    setResults(clientHits.slice(0, 15));

    clearTimeout(debounceRef.current);
    setSearching(true);
    debounceRef.current = setTimeout(() => {
      apiSearch(q)
        .then(raw => {
          const mapped = mapBackend(raw);
          setResults(mapped.length ? mapped : clientHits.slice(0, 15));
        })
        .catch(() => {
          setResults(clientHits.slice(0, 15));
        })
        .finally(() => setSearching(false));
    }, 300);

    return () => clearTimeout(debounceRef.current);
  }, [query, clientIndex]);

  useEffect(() => { setCursor(0); }, [results]);

  const onKeyDown = useCallback((e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setCursor(c => Math.min(c + 1, results.length - 1)); }
    if (e.key === 'ArrowUp')   { e.preventDefault(); setCursor(c => Math.max(c - 1, 0)); }
    if (e.key === 'Enter' && results[cursor]) { closeSearch(); navigate(results[cursor].href); }
  }, [results, cursor, closeSearch, navigate]);

  const grouped = useMemo(() => {
    const g = {};
    results.forEach(r => { if (!g[r.group]) g[r.group] = []; g[r.group].push(r); });
    return GROUP_ORDER.filter(k => g[k]).map(k => ({ label: k, items: g[k] }));
  }, [results]);

  let flatIdx = 0;

  if (!open) return null;

  return (
    <div className="nx-cmd-veil" onClick={closeSearch} style={{ zIndex: 400 }}>
      <div className="nx-cmd-palette" onClick={e => e.stopPropagation()}>
        <div className="nx-cmd-search-bar">
          <NexusIcon name="Search" size={16} style={{ color: 'var(--nx-text-muted)', flexShrink: 0 }} />
          <input
            ref={inputRef}
            className="nx-cmd-input"
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder={RELEASE_FLAGS.PARTNERS ? "Search organizations, users, partners…" : "Search organizations, users…"}
          />
          {searching && <NexusIcon name="Loader" size={14} style={{ opacity: 0.4, flexShrink: 0 }} />}
          {query && !searching && (
            <button className="nx-cmd-clear" onClick={() => setQuery('')}>
              <NexusIcon name="X" size={13} />
            </button>
          )}
          <kbd className="nx-cmd-esc" onClick={closeSearch}>esc</kbd>
        </div>

        <div className="nx-cmd-results">
          {!query.trim() && (
            <div className="nx-cmd-empty">
              <NexusIcon name="Search" size={28} style={{ opacity: 0.25 }} />
              <span>Type to search across the platform</span>
            </div>
          )}
          {query.trim() && results.length === 0 && !searching && (
            <div className="nx-cmd-empty">
              <NexusIcon name="SearchX" size={28} style={{ opacity: 0.25 }} />
              <span>No results for &ldquo;{query}&rdquo;</span>
            </div>
          )}
          {grouped.map(grp => (
            <div key={grp.label} className="nx-cmd-group">
              <div className="nx-cmd-group-label">
                <NexusIcon name={ICONS[grp.label] || 'Circle'} size={12} />{grp.label}
              </div>
              {grp.items.map(item => {
                const idx = flatIdx++;
                return (
                  <button
                    key={item.id}
                    className={'nx-cmd-item' + (idx === cursor ? ' is-active' : '')}
                    onMouseEnter={() => setCursor(idx)}
                    onClick={() => { closeSearch(); navigate(item.href); }}
                  >
                    <span className="nx-domain-mark" style={{ width: 22, height: 22, fontSize: 9, flexShrink: 0 }}>
                      {item.label.split(' ').map(w => w[0]).slice(0, 2).join('')}
                    </span>
                    <span className="nx-cmd-item-text">
                      <span className="nx-cmd-item-label">{item.label}</span>
                      {item.sub && <span className="nx-cmd-item-sub">{item.sub}</span>}
                    </span>
                    {item.badge && <Pill tone={item.tone}>{String(item.badge).replace(/_/g, ' ')}</Pill>}
                    <NexusIcon name="ArrowRight" size={13} style={{ color: 'var(--nx-text-muted)', marginLeft: 'auto', flexShrink: 0 }} />
                  </button>
                );
              })}
            </div>
          ))}
        </div>

        <div className="nx-cmd-footer">
          <span><kbd>↑↓</kbd> navigate</span>
          <span><kbd>↵</kbd> select</span>
          <span><kbd>esc</kbd> close</span>
          <span style={{ marginLeft: 'auto', opacity: 0.5 }}>
            {results.length > 0 ? results.length + ' result' + (results.length !== 1 ? 's' : '') : ''}
          </span>
        </div>
      </div>
    </div>
  );
}
